import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, InputBase, CircularProgress, Alert } from '@mui/material';
import { Shield } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { setupRecaptcha, sendOtp, verifyOtp, getAuthState } from '../firebase/authHandler';
import AdBanner from '../components/AdBanner';

export default function CustomerLogin() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1); // 1 = Phone, 2 = OTP
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getAuthState().onAuthStateChanged(user => {
      if (user && user.phoneNumber) {
        navigate('/customer-dashboard');
      }
    });
  }, [navigate]);

  useEffect(() => {
    // We do NOT initialize recaptcha here anymore, we do it right before sending OTP.
    return () => {
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (e) {}
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  const handleSendOtp = async () => {
    setError(null);
    if (!phoneNumber || phoneNumber.length < 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    
    setLoading(true);
    try {
      const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;
      
      let appVerifier = window.recaptchaVerifier;
      if (!appVerifier) {
        appVerifier = setupRecaptcha('recaptcha-container');
      }

      await sendOtp(formattedPhone, appVerifier);
      setStep(2);
    } catch (err) {
      console.error("Firebase OTP Error:", err);
      const errorCode = err.code ? `(${err.code})` : "";
      setError(`Failed to send OTP. ${err.message} ${errorCode}`);
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (e) {}
        window.recaptchaVerifier = null;
      }
    }
    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    setError(null);
    if (!otp || otp.length < 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }
    
    setLoading(true);
    try {
      await verifyOtp(otp);
      navigate('/customer-dashboard');
    } catch (err) {
      setError("Invalid OTP. Please try again.");
    }
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)', p: 2 }}>
      <Box sx={{ width: '100%', maxWidth: '400px', background: 'var(--bg-surface)', borderRadius: 4, p: 4, border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-xl)' }}>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4, justifyContent: 'center' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg, #15C358, #109441)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <Shield />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 24, letterSpacing: '-0.03em', color: 'var(--text-primary)' }}>
            Customer Portal
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {step === 1 ? (
          <>
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: 14, mb: 3, textAlign: 'center' }}>
              Enter your mobile number to access your loan statements securely.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', borderRadius: 2, p: 1, mb: 3 }}>
              <Typography sx={{ color: 'var(--text-muted)', px: 1, fontWeight: 600 }}>+91</Typography>
              <InputBase 
                placeholder="Mobile Number" 
                value={phoneNumber} 
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                inputProps={{ maxLength: 10 }}
                sx={{ flex: 1, fontSize: 16, color: 'var(--text-primary)', fontFamily: 'Inter' }}
              />
            </Box>
            <Button 
              fullWidth 
              variant="contained" 
              onClick={handleSendOtp} 
              disabled={loading}
              sx={{ py: 1.5, background: 'linear-gradient(135deg, #15C358, #109441)', fontSize: 15, fontWeight: 600, borderRadius: 2 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
            </Button>
          </>
        ) : (
          <>
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: 14, mb: 3, textAlign: 'center' }}>
              Enter the 6-digit OTP sent to +91 {phoneNumber}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', borderRadius: 2, p: 1, mb: 3 }}>
              <InputBase 
                placeholder="000000" 
                value={otp} 
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                inputProps={{ maxLength: 6 }}
                sx={{ flex: 1, fontSize: 20, color: 'var(--text-primary)', fontFamily: 'Inter', textAlign: 'center', letterSpacing: '0.2em' }}
              />
            </Box>
            <Button 
              fullWidth 
              variant="contained" 
              onClick={handleVerifyOtp} 
              disabled={loading}
              sx={{ py: 1.5, background: 'linear-gradient(135deg, #15C358, #109441)', fontSize: 15, fontWeight: 600, borderRadius: 2 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Login'}
            </Button>
            <Button 
              fullWidth 
              onClick={() => setStep(1)} 
              sx={{ mt: 2, color: 'var(--text-secondary)', textTransform: 'none' }}
            >
              Change Mobile Number
            </Button>
          </>
        )}
        
        <div id="recaptcha-container" style={{ marginTop: '20px' }}></div>

      </Box>

      <Box sx={{ mt: 4, width: '100%', maxWidth: '400px' }}>
        <AdBanner type="horizontal" />
      </Box>

      <Typography sx={{ fontSize: 11, textAlign: 'center', color: 'rgba(255,255,255,0.4)', mt: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        Powered by Biatom Tech Solutions
      </Typography>
    </Box>
  );
}
